import {
  Container,
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  Flex,
  List,
  ListItem,
  HStack,
  Button,
  Tabs,
  TabPanels,
  TabPanel,
  useTab,
  useMultiStyleConfig,
} from '@chakra-ui/react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

import { motion } from 'framer-motion';

import ImageShimmer from '../ImageShimmer/ImageShimmer';

const MotionBox = motion(Box);

const TabTextContent = ({
  section,
}: {
  section: {
    button: string;
    image: string;
    roles: {
      icon: string;
      company: string;
      role: string;
      salary: string;
      showSalary: boolean;
    }[];
    title: string;
    text: string;
    list: string[];
    italic: string;
  };
}) => {
  return (
    <>
      <Heading
        as="h3"
        fontSize={{ base: '2rem', lg: '2.625rem' }}
        lineHeight={{ base: '2.5rem', lg: '3rem' }}
        mb="4"
      >
        {section.title}
      </Heading>
      <Text
        fontSize={{ base: '1rem', lg: '1.125rem' }}
        lineHeight={{ base: '1.25rem', lg: '1.5rem' }}
        mb="1.125rem"
      >
        {section.text}
      </Text>
      {section.list.length > 0 && (
        <List spacing="0.5rem" mb="1.125rem">
          {section.list.map((item) => (
            <ListItem
              key={item}
              fontSize={{ base: '1rem', lg: '1.125rem' }}
              lineHeight={{ base: '1.25rem', lg: '1.5rem' }}
            >
              <Flex alignItems="center">
                <svg
                  width="23"
                  height="23"
                  viewBox="0 0 23 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 12.3113L10.5 15.3113L15.5 9.31128"
                    stroke="#25383F"
                    strokeWidth="2"
                    // strokeWnecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.5 21.8113C17.0228 21.8113 21.5 17.3341 21.5 11.8113C21.5 6.28843 17.0228 1.81128 11.5 1.81128C5.97715 1.81128 1.5 6.28843 1.5 11.8113C1.5 17.3341 5.97715 21.8113 11.5 21.8113Z"
                    stroke="#25383F"
                    strokeWidth="2"
                  />
                </svg>

                <Box as="span" ml="0.625rem">
                  {item}
                </Box>
              </Flex>
            </ListItem>
          ))}
        </List>
      )}
      <Box
        as="em"
        display="inline-block"
        fontSize={{ base: '1rem', lg: '1.125rem' }}
        lineHeight={{ base: '1.25rem', lg: '1.5rem' }}
      >
        {section.italic}
      </Box>
    </>
  );
};

const JobBlock = ({
  item,
  index,
}: {
  item: {
    icon: string;
    company: string;
    role: string;
    salary: string;
    showSalary: boolean;
  };
  index: number;
}) => {
  return (
    <Box
      key={item.company}
      p="4"
      bgColor="white"
      boxShadow="0 0.25rem 0.75rem rgba(0, 0, 0, 0.1)"
      borderRadius="0.8125rem"
      mb="1.5rem"
      w={index === 0 ? '16rem' : '14rem'}
      minH="5.5rem"
      position="relative"
    >
      {index === 0 && (
        <Box
          position="absolute"
          w="0.6875rem"
          h="0.6875rem"
          bgColor="#F96157"
          borderRadius="50%"
          top="4"
          right="4"
        />
      )}
      <Flex alignItems="center">
        <Box
          position="relative"
          w="3.5rem"
          h="3.5rem"
          borderRadius="0.5rem"
          overflow="hidden"
          mr="0.5625rem"
        >
          <ImageShimmer src={item.icon} alt={item.company} style={{ objectFit: 'cover' }} fill />
        </Box>
        <Box>
          <Box
            fontSize={index === 0 ? '1.25rem' : '1.125rem'}
            fontWeight="semibold"
            mr="2"
            lineHeight="1"
          >
            {item.company}
          </Box>
          <Box
            mt={index === 0 && '1'}
            fontSize={index === 0 ? '1rem' : '0.875rem'}
            fontWeight="semibold"
            lineHeight="1"
            color="grey.500"
          >
            {item.role}
          </Box>
          {index !== 0 && item.showSalary && (
            <Box
              display="inline-block"
              fontSize="0.625rem"
              fontWeight="semibold"
              p="0.125rem 0.375rem"
              bgColor="#F1F4FB"
              color="grey.600"
              borderRadius="2.0625rem"
            >
              {item.salary}
            </Box>
          )}
        </Box>
      </Flex>
      {index === 0 && item.showSalary && (
        <Box
          display="inline-block"
          fontSize="0.875rem"
          fontWeight="semibold"
          mt="6"
          p="0.375rem"
          bgColor="#C7F3DD"
          color="grey.900"
          borderRadius="0.625rem"
          w="100%"
          textAlign="center"
        >
          {item.salary}
        </Box>
      )}
    </Box>
  );
};

const CustomTabs = ({
  tabs,
}: {
  tabs: {
    button: string;
    image: string;
    roles: {
      icon: string;
      company: string;
      role: string;
      salary: string;
      showSalary: boolean;
    }[];
    title: string;
    text: string;
    list: string[];
    italic: string;
  }[];
}) => {
  const CustomTab = (props) => {
    const tabProps = useTab({ ...props });
    const isSelected = !!tabProps['aria-selected'];
    const styles = useMultiStyleConfig('Tabs', tabProps);
    return (
      <Button
        __css={styles.tab}
        {...tabProps}
        py="0"
        px="0"
        textAlign="left"
        position="relative"
        border="none"
        _before={{
          content: '""',
          position: 'absolute',

          margin: '0 auto',
          bottom: '0',
          width: '100%',
          height: '0.125rem',
          bgColor: isSelected ? 'grey.900' : 'transparent',
        }}
        _active={{
          bgColor: 'transparent',
        }}
        _focus={{
          boxShadow: isSelected ? 'none' : 'var(--chakra-shadows-outline)',
        }}
      >
        <Box fontSize="1.125rem" lineHeight="10" color={isSelected ? 'grey.900' : 'grey.900'}>
          {props.title}
        </Box>
      </Button>
    );
  };

  return (
    <Box display={{ base: 'none', lg: 'block' }}>
      <Tabs isLazy id="tabs" variant="unstyled">
        <HStack spacing="4.625rem" justifyContent="center">
          {tabs.map((tab, index) => (
            <CustomTab key={index} title={tab.button} />
          ))}
        </HStack>
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel key={index} mt="9" overflow="hidden">
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Grid gridTemplateColumns="1fr 1fr">
                  <GridItem mr={{ lg: '2rem', xl: '11.25rem' }}>
                    <TabTextContent section={tab} />
                  </GridItem>
                  <GridItem position="relative">
                    {tab.roles?.length > 0 && (
                      <Box position="absolute" left="0" zIndex="1">
                        {tab.roles.map((role) => (
                          <JobBlock item={role} index={index} key={role.company} />
                        ))}
                      </Box>
                    )}
                    <ImageShimmer
                      src={tab.image}
                      alt=""
                      width="620"
                      height="440"
                      style={{ objectFit: 'contain' }}
                      layout="intrinsic"
                    />
                  </GridItem>
                </Grid>
              </MotionBox>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default function LightTabs({
  tabs,
}: {
  tabs: {
    title: string;
    sections: {
      button: string;
      image: string;
      roles: {
        icon: string;
        company: string;
        role: string;
        salary: string;
        showSalary: boolean;
      }[];
      title: string;
      text: string;
      list: string[];
      italic: string;
    }[];
  };
}) {
  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return '<span class="' + className + '"></span>';
    },
  };

  return (
    <Box my={{ base: '12', lg: '24' }} id="about">
      <Container>
        <Heading
          as="h2"
          textAlign="center"
          fontSize={{ base: '2.5rem', lg: '3rem' }}
          lineHeight={{ base: '3rem', lg: '3.5rem' }}
          mb={{ base: '4', lg: '6' }}
        >
          {tabs.title}
        </Heading>
        <Box display={{ lg: 'none' }}>
          <style global jsx>{`
            .swiper-horizontal .swiper-pagination {
              bottom: 0;
            }
            .swiper-pagination-horizontal.swiper-pagination-bullets .swiper-pagination-bullet {
              width: 1rem;
              height: 1rem;
              margin: 0 0.5rem;
              background-color: #43b97f;
            }
          `}</style>
          <Box
            as={Swiper}
            py="4"
            pb="16"
            pagination={pagination}
            modules={[Pagination, Autoplay]}
            autoplay={{
              delay: 10000,
              disableOnInteraction: false,
            }}
            speed={1000}
            autoHeight={true}
          >
            {tabs.sections.map((section, index) => (
              <SwiperSlide key={index} style={{ width: '20.8125rem' }}>
                <Box
                  key={section.button}
                  minH={{ base: '37.5rem', sm: '41.9375rem', md: '44.5rem' }}
                >
                  <Box>
                    <Box mb="6" position="relative">
                      {section.roles?.length > 0 && (
                        <Box position="absolute" left="3" zIndex="1">
                          {section.roles.map((role) => (
                            <JobBlock item={role} index={index} key={role.company} />
                          ))}
                        </Box>
                      )}
                      <ImageShimmer
                        src={section.image}
                        alt=""
                        width="620"
                        height="440"
                        style={{ objectFit: 'contain' }}
                        layout="intrinsic"
                      />
                    </Box>
                    <TabTextContent section={section} />
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Box>
        </Box>
        <CustomTabs tabs={tabs.sections} />
      </Container>
    </Box>
  );
}
